import { normalizeStatesOfferedToCodes } from 'helpers/carriers/carriers';
import { getCompany } from 'helpers/getCompanyVariables';

// helpers for the OEP Updates view. keeps the expansion/exit split logic and
// the status-derived aggregations in a single place so the sidebar summary,
// the main updates panel, and the "all available states" grid never drift
// apart on how they read the subform.

// legend copy + lucide icon used on the Carrier Appointments card badge and
// legend row. kept here so both renderers share one source of truth.
export const OEP_UPDATE_ICON_NAME = 'megaphone';
export const OEP_UPDATE_ICON_COLOR = '#d97706';
export const OEP_UPDATE_ICON_DESCRIPTION = 'OEP Updates Available';

/**
 * decides whether the OEP update icon (and detail button) should be shown
 * for a carrier on the shared Carrier Appointments screen. mirrors the sidebar
 * filter in OepUpdates.jsx so the two never drift apart: only O'Neill, only
 * state-based carriers, never when explicitly hidden, only inside the OEP
 * window (driven by the OEP_Start_Date/OEP_End_Date org variables), and only
 * when there is actually something to show.
 * @param carrier - normalized carrier from carrierFactory
 * @param user - zohoData user (needs the isOepWindowActive flag). when
 *   missing we fail closed so the icon/button never leaks outside the window.
 * @returns boolean
 */
export function shouldShowOepUpdateIcon(carrier, user) {
  if (!carrier) return false;
  if (getCompany() !== 'oneill') return false;
  if (!user?.isOepWindowActive) return false;
  if (carrier.oepHidden) return false;
  if (carrier.considerStates !== 'Yes') return false;
  return hasOepUpdates(carrier);
}

/**
 * splits a carrier's normalized oep state updates into two arrays by TYPE
 * (semantic bucket the ui shows as "Expansions" and "Exits"). the finer
 * status (Added / Updated / Removed) lives inside each row. rows are
 * deduped by stateCode within each section following SCOPE_PRIORITY so agents
 * never see two "VA" chips in the same section.
 * @param carrier - normalized carrier from carrierFactory
 * @returns object with `expansions` and `exits` arrays; empty when the carrier
 *   has no updates or isn't oep-active
 */
export function splitOepStateUpdates(carrier) {
  const rows = carrier?.oepStateUpdates || [];
  const expansionsByState = new Map();
  const exitsByState = new Map();
  for (const row of rows) {
    const bucket = pickSectionBucket(row.type, expansionsByState, exitsByState);
    if (!bucket) continue;
    const existing = bucket.get(row.stateCode);
    if (!existing || shouldReplaceRow(row, existing)) {
      bucket.set(row.stateCode, row);
    }
  }
  return {
    expansions: Array.from(expansionsByState.values()),
    exits: Array.from(exitsByState.values())
  };
}

function pickSectionBucket(type, expansionsByState, exitsByState) {
  if (type === 'Expansion') return expansionsByState;
  if (type === 'Exit') return exitsByState;
  return null;
}

// ranks scopes by how much the detail panel can say about them. the
// highest-ranked row for a state wins the dedupe within a section: an explicit
// `New State` beats a county list, which in turn beats a bare `Whole State`.
const SCOPE_PRIORITY = { 'New State': 3, 'Counties Only': 2, 'Whole State': 1 };

function shouldReplaceRow(incoming, existing) {
  return scopeRank(incoming) > scopeRank(existing);
}

function scopeRank(row) {
  return SCOPE_PRIORITY[row?.scope] || 0;
}

/**
 * true when the carrier published a free-form note for this OEP. kept separate
 * from the state counts so the notes panel and hasOepUpdates read emptiness the
 * same way (a whitespace-only field counts as empty).
 * @param carrier - normalized carrier from carrierFactory
 * @returns boolean
 */
export function hasOepCarrierNote(carrier) {
  return !!carrier?.oepNotes?.trim();
}

/**
 * true when the carrier has anything meaningful to show on the OEP Updates
 * page: at least one state with a resolved status (Added/Updated/Removed) in
 * the OEP subform, or a carrier note. used to sort carriers with actual
 * updates ahead of the "no updates yet" ones in the sidebar and to decide
 * whether the megaphone icon shows on the Carrier Appointments card.
 * a note alone counts: a carrier can have nothing to announce state-wise and
 * still have something it wants agents to read.
 * @param carrier - normalized carrier from carrierFactory
 * @returns boolean
 */
export function hasOepUpdates(carrier) {
  if (hasOepCarrierNote(carrier)) return true;
  const { added, updatedPlus, updatedMinus, updatedMixed, removed } =
    countOepStateUpdates(carrier);
  return added + updatedPlus + updatedMinus + updatedMixed + removed > 0;
}

/**
 * orders carriers for the OEP Updates sidebar: those with any status
 * (Added/Updated/Removed) come first, alphabetically; the rest follow, also
 * alphabetically. keeps the sort out of the presentational sidebar so both
 * the desktop pane and the mobile sheet see the same order.
 * @param carriers - array of normalized carriers
 * @returns new array sorted for the sidebar
 */
export function sortCarriersForOepSidebar(carriers) {
  const withUpdates = [];
  const withoutUpdates = [];
  for (const carrier of carriers) {
    if (hasOepUpdates(carrier)) withUpdates.push(carrier);
    else withoutUpdates.push(carrier);
  }
  const byName = (a, b) => a.name.localeCompare(b.name);
  withUpdates.sort(byName);
  withoutUpdates.sort(byName);
  return [...withUpdates, ...withoutUpdates];
}

/**
 * counts DISTINCT STATES per resolved status for the sidebar summary badges,
 * using the same rules as buildStateStatusMap so the sidebar never disagrees
 * with the "all available states" grid or the deduped Expansions/Exits chips.
 * the `Updated` bucket is split by direction so the sidebar can tell at a
 * glance whether an existing state is gaining counties (+), losing counties
 * (−), or both (±):
 *  - updatedPlus: Updated states with only Expansion rows
 *  - updatedMinus: Updated states with only Exit rows
 *  - updatedMixed: Updated states with both Expansion and Exit rows
 * @param carrier - normalized carrier from carrierFactory
 * @returns object with `added`, `updatedPlus`, `updatedMinus`, `updatedMixed`,
 *   `removed` counts
 */
export function countOepStateUpdates(carrier) {
  const { statusByCode } = buildStateStatusMap(carrier);
  const rowsByState = groupRowsByState(carrier?.oepStateUpdates || []);
  let added = 0;
  let updatedPlus = 0;
  let updatedMinus = 0;
  let updatedMixed = 0;
  let removed = 0;
  for (const [code, status] of Object.entries(statusByCode)) {
    if (status === 'Added') added += 1;
    else if (status === 'Removed') removed += 1;
    else if (status === 'Updated') {
      const rows = rowsByState[code] || [];
      const hasExpansion = rows.some(r => r.type === 'Expansion');
      const hasExit = rows.some(r => r.type === 'Exit');
      if (hasExpansion && hasExit) updatedMixed += 1;
      else if (hasExpansion) updatedPlus += 1;
      else if (hasExit) updatedMinus += 1;
    }
  }
  return { added, updatedPlus, updatedMinus, updatedMixed, removed };
}

/**
 * builds a map of stateCode -> status for the "all available states" grid,
 * unioning the carrier's existing coverage with any oep updates. rules:
 *  - if any oep row for the state is `Exit + Whole State` → Removed (red).
 *    beats every other status; the state is being fully dropped regardless
 *    of whether it currently appears in States_Offered.
 *  - else if any row is `Expansion + New State` → Added (green). the admin
 *    explicitly flagged new coverage, which outranks States_Offered.
 *  - else if the state IS in States_Offered → Updated (amber). existing
 *    coverage that has some oep activity (county-level changes, whole-state
 *    expansion of an already-offered state, etc.) reads as an "update".
 *  - else the state has oep activity but is NOT in States_Offered → Added
 *    (green). it's coming in as new coverage.
 *  - states in States_Offered with no oep rows → null (neutral gray).
 * @param carrier - normalized carrier from carrierFactory
 * @returns object with:
 *   - `codes`: string[] sorted union of state codes to render
 *   - `statusByCode`: Record<stateCode, 'Added'|'Updated'|'Removed'|null>
 */
export function buildStateStatusMap(carrier) {
  const offeredCodes = new Set(
    normalizeStatesOfferedToCodes(carrier?.statesOffered)
  );
  const rowsByState = groupRowsByState(carrier?.oepStateUpdates || []);
  const statusByCode = {};
  for (const code of offeredCodes) {
    statusByCode[code] = null;
  }
  for (const [code, rows] of Object.entries(rowsByState)) {
    statusByCode[code] = resolveStateStatus({
      rows,
      isOffered: offeredCodes.has(code)
    });
  }
  const codes = Object.keys(statusByCode).sort();
  return { codes, statusByCode };
}

function groupRowsByState(rows) {
  const byState = {};
  for (const row of rows) {
    if (!byState[row.stateCode]) byState[row.stateCode] = [];
    byState[row.stateCode].push(row);
  }
  return byState;
}

function resolveStateStatus({ rows, isOffered }) {
  if (rows.length === 0) return null;
  const hasWholeStateExit = rows.some(
    r => r.type === 'Exit' && r.scope === 'Whole State'
  );
  if (hasWholeStateExit) return 'Removed';
  // an explicit `New State` expansion outranks States_Offered: the admin has
  // stated this is new coverage, and States_Offered is often updated ahead of
  // the OEP window opening, which would otherwise flip the chip to amber.
  const hasNewState = rows.some(
    r => r.type === 'Expansion' && r.scope === 'New State'
  );
  if (hasNewState) return 'Added';
  if (isOffered) return 'Updated';
  // defensive: a counties-only Exit on a state that isn't in States_Offered
  // isn't semantically an "Added" — treat as Updated so it doesn't inflate the
  // green "+N new states" count in the sidebar.
  if (rows.some(r => r.type === 'Exit')) return 'Updated';
  return 'Added';
}
